import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { CharacterCountBar } from "./CharacterCountBar";
import { META_LIMITS, TITLE_LIMITS } from "./seoCharLimits";
import type {
  PageIssue,
  ProposedFix,
  TitleMetaFix,
} from "@/types/technicalAudit";

// Editable proposed SEO title / meta description for the title-meta family of
// Group-1 issues (title_too_short, duplicate_title, serp_title_truncated,
// duplicate_meta, serp_meta_truncated). It mirrors the TextDiffCard idiom but
// reads from the simpler details shapes those issue types use, and only ever
// emits the field(s) its issue type is allowed to change so a meta-only issue
// can never overwrite the SEO title (and vice versa).

const TITLE_TYPES = new Set([
  "title_too_short",
  "duplicate_title",
  "serp_title_truncated",
  // Atomic TITLE-ONLY runtime types. Only ever render the title field so a
  // title issue can never overwrite the meta description.
  "page_title_same_as_h1",
  "page_title_missing",
  "page_title_over_60_chars",
  "page_title_below_30_chars",
]);
const META_TYPES = new Set([
  "duplicate_meta",
  "serp_meta_truncated",
  // Atomic META-ONLY runtime types. Only ever render the meta field so a meta
  // issue can never overwrite the SEO title.
  "meta_missing",
  "meta_description_over_155_chars",
  "meta_description_below_70_chars",
]);

interface SeoMetaEditCardProps {
  issue: PageIssue;
  onEdit: (value: ProposedFix) => void;
}

// The crawl detail shapes differ per type, so read the current value leniently.
function currentTitleOf(details: Record<string, unknown>): string {
  return (details.current_title as string) || (details.title as string) || "";
}

function currentMetaOf(details: Record<string, unknown>): string {
  return (
    (details.current_meta_description as string) ||
    (details.current_meta as string) ||
    (details.description as string) ||
    ""
  );
}

export function SeoMetaEditCard({ issue, onEdit }: SeoMetaEditCardProps) {
  const showTitle = TITLE_TYPES.has(issue.issue_type);
  const showMeta = META_TYPES.has(issue.issue_type);
  const details = (issue.issue_details || {}) as Record<string, unknown>;
  const proposed = issue.proposed_fix as
    | (TitleMetaFix & { reasoning?: string })
    | null;
  const approved = issue.approved_value as
    | (TitleMetaFix & { reasoning?: string })
    | null;
  // Prefer the canonical `new_seo_title`; fall back to legacy `new_title` so
  // suggestions stored before the split still display.
  const proposedTitle = proposed?.new_seo_title || proposed?.new_title || "";
  const approvedTitle = approved?.new_seo_title || approved?.new_title || "";
  const draftKey = `${issue.id}:${issue.issue_type}:${JSON.stringify(issue.approved_value ?? issue.proposed_fix ?? "")}`;
  const initialDraft = {
    title: showTitle ? approvedTitle || proposedTitle : "",
    meta: showMeta
      ? approved?.new_meta_description || proposed?.new_meta_description || ""
      : "",
  };

  const [draft, setDraft] = useState<{
    key: string;
    title: string;
    meta: string;
  } | null>(null);
  const editedTitle =
    draft?.key === draftKey ? draft.title : initialDraft.title;
  const editedMeta = draft?.key === draftKey ? draft.meta : initialDraft.meta;

  // Push the draft up so Approve/Apply send exactly what the specialist sees.
  useEffect(() => {
    const value: TitleMetaFix = {};
    if (showTitle) {
      const t = editedTitle.trim() || undefined;
      // Canonical SEO/meta title key; mirror to new_title for back-compat
      // readers, and stamp target_field so the backend apply chokepoint can
      // confirm this is a SEO/meta title write (never the page title or H1).
      value.new_seo_title = t;
      value.new_title = t;
      value.target_field = "seo_title";
    }
    if (showMeta) value.new_meta_description = editedMeta.trim() || undefined;
    onEdit(value as ProposedFix);
  }, [editedTitle, editedMeta, showTitle, showMeta, onEdit]);

  const hasProposed = Boolean(
    approvedTitle ||
    approved?.new_meta_description ||
    proposedTitle ||
    proposed?.new_meta_description,
  );
  const hasStoredSuggestion = Boolean(
    issue.approved_value || issue.proposed_fix || issue.proposed_value,
  );
  const currentTitle = currentTitleOf(details);
  const currentMeta = currentMetaOf(details);
  const reasoning = approved?.reasoning || proposed?.reasoning;

  const fieldLabel =
    showTitle && showMeta
      ? "title & meta description"
      : showTitle
        ? "title"
        : "meta description";

  if (!hasProposed) {
    const actionLabel = hasStoredSuggestion ? "Regenerate fix" : "Generate fix";
    const actionVerb = hasStoredSuggestion
      ? "replace the stored placeholder with"
      : "draft";

    return (
      <Card>
        <CardContent className="py-4 text-sm text-muted-foreground">
          Click <span className="font-medium">{actionLabel}</span> above to{" "}
          {actionVerb} an SEO-friendly {fieldLabel}, then edit it and apply it
          to the SEO/meta title tag (Yoast/RankMath) — not the visible page
          title or H1.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          Proposed SEO{" "}
          {showTitle && showMeta
            ? "Title & Meta"
            : showTitle
              ? "Title"
              : "Meta Description"}
        </CardTitle>
        {showTitle && (
          <p className="text-xs text-muted-foreground">
            Updates the SEO/meta title tag (Yoast/RankMath) shown in search
            results — not the visible WordPress page title or the on-page H1. A
            "Primary Keyword | Brand" format is fine here when it fits the length.
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {showTitle && (
          <div className="space-y-2">
            {currentTitle && (
              <div>
                <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Current SEO title
                </div>
                <div className="rounded-md border bg-muted/50 p-2 text-sm">
                  {currentTitle}
                </div>
              </div>
            )}
            <div>
              <div className="mb-1 text-sm font-medium text-green-600 dark:text-green-400">
                Proposed SEO title (editable)
              </div>
              <Textarea
                value={editedTitle}
                onChange={(e) =>
                  setDraft({
                    key: draftKey,
                    title: e.target.value,
                    meta: editedMeta,
                  })
                }
                className="min-h-[70px]"
                placeholder="Edit the proposed SEO title..."
              />
              <div className="mt-2">
                <CharacterCountBar
                  current={editedTitle.trim().length}
                  min={TITLE_LIMITS.min}
                  max={TITLE_LIMITS.max}
                  optimal={TITLE_LIMITS.optimal}
                />
              </div>
            </div>
          </div>
        )}

        {showMeta && (
          <div className="space-y-2">
            {currentMeta && (
              <div>
                <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Current description
                </div>
                <div className="rounded-md border bg-muted/50 p-2 text-sm">
                  {currentMeta}
                </div>
              </div>
            )}
            <div>
              <div className="mb-1 text-sm font-medium text-green-600 dark:text-green-400">
                Proposed meta description (editable)
              </div>
              <p className="mb-1 text-xs text-muted-foreground">
                Apply writes this as the page's meta description (Yoast/RankMath) —
                the snippet shown under the title in search results.
              </p>
              <Textarea
                value={editedMeta}
                onChange={(e) =>
                  setDraft({
                    key: draftKey,
                    title: editedTitle,
                    meta: e.target.value,
                  })
                }
                className="min-h-[100px]"
                placeholder="Edit the proposed meta description..."
              />
              <div className="mt-2">
                <CharacterCountBar
                  current={editedMeta.trim().length}
                  min={META_LIMITS.min}
                  max={META_LIMITS.max}
                  optimal={META_LIMITS.optimal}
                />
              </div>
            </div>
          </div>
        )}

        {reasoning && (
          <div className="rounded-md bg-blue-50 dark:bg-blue-950/40 p-3">
            <p className="text-xs font-medium text-blue-800 dark:text-blue-300">AI Reasoning:</p>
            <p className="mt-1 text-xs text-blue-700 dark:text-blue-400">{reasoning}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
